import React, { useState, useEffect } from "react";
import { CATALOG, CATS } from "../data/catalog";
import { THUMB_IMAGES } from "../data/images";

const REQ_COLORS = {
  "220V": "#ff6644",
  "110V": "#ffaa44",
  "Air Supply": "#44aaff",
  "Concrete Anchor": "#aa88ff",
  Drainage: "#44ddbb",
  Ventilation: "#ffdd44",
  "Min 11ft Ceiling": "#ff88aa",
  "Min 12ft Ceiling": "#ff88aa",
  "Min 24ft Bay Depth": "#ff88aa",
};

const ROOM_COLORS = [
  { label: "Yellow", color: "rgba(255,229,0,0.08)", border: "rgba(255,229,0,0.6)" },
  { label: "Blue", color: "rgba(0,150,255,0.08)", border: "rgba(0,150,255,0.6)" },
  { label: "Green", color: "rgba(0,200,100,0.08)", border: "rgba(0,200,100,0.6)" },
  { label: "Red", color: "rgba(255,80,80,0.08)", border: "rgba(255,80,80,0.6)" },
  { label: "Purple", color: "rgba(170,100,255,0.08)", border: "rgba(170,100,255,0.6)" },
];

// Short labels for category icon strip when sidebar is collapsed
const CAT_ABBR = {
  Lifts: "LIFT",
  Tire: "TIRE",
  Air: "AIR",
  Service: "SVC",
  Storage: "STOR",
  Structure: "STR",
};

const makeRoom = (name, shop) => ({
  uid: `${Date.now()}-${Math.random()}`,
  isRoom: true,
  id: "room",
  name,
  label: name,
  cx: shop.w / 2,
  cy: shop.h / 2,
  fw: Math.min(14, Math.round(shop.w * 0.2)),
  fh: Math.min(12, Math.round(shop.h * 0.25)),
  scaleX: 1,
  scaleY: 1,
  rot: 0,
  color: ROOM_COLORS[0].color,
  border: ROOM_COLORS[0].border,
  _dragging: false,
});

export default function Sidebar({
  placed,
  selected,
  setPlaced,
  setSelected,
  drains,
  setDrains,
  selectedDrain,
  setSelectedDrain,
  shop,
  roomDrawMode,
  setRoomDrawMode,
  commitHistory,
  onAddItem,          // click-to-add: App forwards to CadCanvas.addCatalogItem
}) {
  const [activeCat, setActiveCat] = useState("Lifts");
  const [hovered, setHovered] = useState(null);
  const [roomName, setRoomName] = useState("");

  // Collapsed state — persists in localStorage so user prefs stick
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sb_sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("sb_sidebar_collapsed", collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  // ─── Undo history for sidebar edits ───────────────────────────
  // Sidebar actions (rotate, remove, align, typed size/position, drains,
  // rooms) used to skip history entirely. Rather than wire every handler,
  // the expanded panel calls this on any click or input blur. commitHistory
  // skips no-op snapshots, so extra calls are harmless. setTimeout lets
  // React apply the state update first so the snapshot sees the new values.
  const commitSoon = () => {
    if (commitHistory) setTimeout(commitHistory, 0);
  };

  const selItems = placed.filter((p) => selected.includes(p.uid));
  const selItem = selItems.length === 1 ? selItems[0] : null;
  const selDrain = drains.find((d) => d.id === selectedDrain);
  const multiSel = selItems.length > 1;

  // ===== handlers (unchanged from before) =====
  const rotate = (deg) =>
    setPlaced((prev) =>
      prev.map((p) => (selected.includes(p.uid) ? { ...p, rot: (p.rot + deg + 360) % 360 } : p))
    );
  const reset = () =>
    setPlaced((prev) =>
      prev.map((p) => (selected.includes(p.uid) ? { ...p, rot: 0, scaleX: 1, scaleY: 1 } : p))
    );
  const remove = () => {
    setPlaced((prev) => prev.filter((p) => !selected.includes(p.uid)));
    setSelected([]);
  };
  const setLabel = (val) =>
    setPlaced((prev) =>
      prev.map((p) => (p.uid === selItem?.uid ? { ...p, label: val, name: val } : p))
    );
  const setItemW = (val) => {
    const num = parseFloat(val);
    if (!num || num <= 0 || !selItem) return;
    setPlaced((prev) =>
      prev.map((p) => (p.uid === selItem.uid ? { ...p, scaleX: num / p.fw } : p))
    );
  };
  const setItemH = (val) => {
    const num = parseFloat(val);
    if (!num || num <= 0 || !selItem) return;
    setPlaced((prev) =>
      prev.map((p) => (p.uid === selItem.uid ? { ...p, scaleY: num / p.fh } : p))
    );
  };
  const setItemX = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || !selItem) return;
    setPlaced((prev) => prev.map((p) => {
      if (p.uid !== selItem.uid) return p;
      // Polygon rooms: translate vertices by the delta so the shape moves
      // rather than just the centroid sliding away from its outline.
      if (p.shapeType === 'free' && p.vertices) {
        const dx = num - p.cx;
        return { ...p, cx: num, vertices: p.vertices.map(v => ({ x: v.x + dx, y: v.y })) };
      }
      return { ...p, cx: num };
    }));
  };
  const setItemY = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || !selItem) return;
    setPlaced((prev) => prev.map((p) => {
      if (p.uid !== selItem.uid) return p;
      if (p.shapeType === 'free' && p.vertices) {
        const dy = num - p.cy;
        return { ...p, cy: num, vertices: p.vertices.map(v => ({ x: v.x, y: v.y + dy })) };
      }
      return { ...p, cy: num };
    }));
  };

  const align = (type) => {
    if (selItems.length < 2) return;
    // Polygon rooms are EXCLUDED from align/distribute in v1 — these operations
    // assume rect bbox math (cx ± fw/2) and setting cx alone would decouple the
    // centroid from the actual vertex positions. We skip them silently; the user
    // can still align/distribute the rect items in their selection.
    setPlaced((prev) => {
      const targets = prev.filter((p) => selected.includes(p.uid) && p.shapeType !== 'free');
      if (targets.length < 2) return prev;   // nothing to align after excluding polygons
      const lefts = targets.map((p) => p.cx - (p.fw * p.scaleX) / 2);
      const rights = targets.map((p) => p.cx + (p.fw * p.scaleX) / 2);
      const tops = targets.map((p) => p.cy - (p.fh * p.scaleY) / 2);
      const bottoms = targets.map((p) => p.cy + (p.fh * p.scaleY) / 2);
      const minLeft = Math.min(...lefts);
      const maxRight = Math.max(...rights);
      const minTop = Math.min(...tops);
      const maxBottom = Math.max(...bottoms);
      const centerX = (minLeft + maxRight) / 2;
      const centerY = (minTop + maxBottom) / 2;
      const sortedH = [...targets].sort((a, b) => a.cx - b.cx);
      const sortedV = [...targets].sort((a, b) => a.cy - b.cy);
      return prev.map((p) => {
        // Polygon rooms in selection are not modified
        if (!selected.includes(p.uid) || p.shapeType === 'free') return p;
        const hw = (p.fw * p.scaleX) / 2;
        const hh = (p.fh * p.scaleY) / 2;
        switch (type) {
          case "left": return { ...p, cx: minLeft + hw };
          case "right": return { ...p, cx: maxRight - hw };
          case "centerH": return { ...p, cx: centerX };
          case "top": return { ...p, cy: minTop + hh };
          case "bottom": return { ...p, cy: maxBottom - hh };
          case "centerV": return { ...p, cy: centerY };
          case "distH": {
            const idx = sortedH.findIndex((s) => s.uid === p.uid);
            const span = sortedH[sortedH.length - 1].cx - sortedH[0].cx;
            return { ...p, cx: sortedH[0].cx + idx * (span / (sortedH.length - 1)) };
          }
          case "distV": {
            const idx = sortedV.findIndex((s) => s.uid === p.uid);
            const span = sortedV[sortedV.length - 1].cy - sortedV[0].cy;
            return { ...p, cy: sortedV[0].cy + idx * (span / (sortedV.length - 1)) };
          }
          default: return p;
        }
      });
    });
  };

  const addDrain = () => {
    const id = `drain-${Date.now()}`;
    setDrains((prev) => [
      ...prev,
      { id, cx: shop.w / 2, cy: shop.h / 2, w: 2, h: 2, rot: 0, label: "Floor Drain" },
    ]);
    setSelectedDrain(id);
    setSelected([]);
  };

  const addRoom = () => {
    const name = roomName.trim() || "Room";
    const room = makeRoom(name, shop);
    setPlaced((prev) => [room, ...prev]);
    setSelected([room.uid]);
    setSelectedDrain(null);
    setRoomName("");
  };

  const removeDrain = () => {
    setDrains((prev) => prev.filter((d) => d.id !== selectedDrain));
    setSelectedDrain(null);
  };

  const setDrainProp = (key, val) =>
    setDrains((prev) => prev.map((d) => (d.id === selectedDrain ? { ...d, [key]: val } : d)));

  const isLift = (eq) =>
    ["2post", "4post", "scissor", "inground", "mobile_column"].includes(eq?.id);

  // Click a category icon while collapsed → expand AND switch to that category
  const pickCategory = (cat) => {
    setActiveCat(cat);
    setCollapsed(false);
  };

  // ===== COLLAPSED VIEW — thin icon strip =====
  if (collapsed) {
    return (
      <div
        style={{
          width: 52,
          background: "#07111e",
          borderRight: "1px solid #162840",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          transition: "width .2s ease",
        }}
      >
        {/* Expand toggle at top */}
        <button
          onClick={() => setCollapsed(false)}
          title="Expand library"
          style={{
            background: "transparent",
            border: "none",
            borderBottom: "1px solid #162840",
            color: "#3399ff",
            fontSize: 16,
            fontWeight: 700,
            padding: "10px 0",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ›
        </button>

        {/* Category buttons stacked vertically */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, padding: "8px 4px" }}>
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => pickCategory(c)}
              title={`${c} — click to expand`}
              style={{
                background: "rgba(0,80,180,.08)",
                border: "1px solid #162840",
                borderRadius: 4,
                color: "#a8c4dc",
                fontSize: 11,
                fontWeight: 700,
                padding: "8px 2px",
                cursor: "pointer",
                fontFamily: "inherit",
                letterSpacing: ".05em",
                writingMode: "horizontal-tb",
              }}
            >
              {CAT_ABBR[c] || c}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ===== EXPANDED VIEW — full sidebar (mostly original code) =====
  return (
    <div
      onClick={commitSoon}   // after any button action in the panel → one undo step
      onBlur={commitSoon}    // after typing in a field (label, size, position) → one undo step
      style={{
        width: 260,   // was 234 — room for the + buttons and larger text
        background: "#07111e",
        borderRight: "1px solid #162840",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        transition: "width .2s ease",
      }}
    >
      {/* Header with collapse toggle */}
      <div
        style={{
          padding: "10px 12px",
          fontSize: 11,
          fontWeight: 700,
          color: "#3399ff",
          letterSpacing: ".1em",
          textTransform: "uppercase",
          borderBottom: "1px solid #162840",
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Equipment Library</span>
        <button
          onClick={() => setCollapsed(true)}
          title="Collapse library"
          style={{
            background: "transparent",
            border: "none",
            color: "#3399ff",
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
            padding: "0 4px",
            fontFamily: "inherit",
            lineHeight: 1,
          }}
        >
          ‹
        </button>
      </div>

      {/* Category tabs */}
      <div
        style={{
          display: "flex",
          gap: 3,
          padding: "7px 8px",
          borderBottom: "1px solid #162840",
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCat(c)}
            style={{
              background: activeCat === c ? "rgba(0,80,180,.18)" : "transparent",
              border: `1px solid ${activeCat === c ? "#0066bb88" : "#162840"}`,
              borderRadius: 4,
              color: activeCat === c ? "#3399ff" : "#a8c4dc",
              fontSize: 11,
              fontWeight: 600,
              padding: "3px 8px",
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: ".05em",
              textTransform: "uppercase",
            }}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Equipment list */}
      <div style={{ flex: 1, overflow: "auto", padding: "6px 6px" }}>
        {CATALOG.filter((e) => e.cat === activeCat).map((eq) => (
          <div
            key={eq.id}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("eqId", eq.id)}
            onMouseEnter={() => setHovered(eq)}
            onMouseLeave={() => setHovered(null)}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: "8px 6px",
              borderRadius: 6,
              cursor: "grab",
              marginBottom: 3,
              border: "1px solid transparent",
              background: hovered?.id === eq.id ? "rgba(0,100,180,.07)" : "transparent",
              borderColor: hovered?.id === eq.id ? "#1e3a5466" : "transparent",
              transition: "all .12s",
            }}
          >
            {/* Thumbnail */}
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 5,
                border: `1px solid ${eq.border}`,
                background: eq.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {THUMB_IMAGES[eq.id] ? (
                <img
                  src={THUMB_IMAGES[eq.id]}
                  alt={eq.name}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: eq.border,
                    fontFamily: "monospace",
                    textAlign: "center",
                    lineHeight: 1.2,
                    padding: 2,
                  }}
                >
                  {eq.fw}×{eq.fh}
                </span>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#e8f4ff", lineHeight: 1.3 }}>
                {eq.name}
              </div>
              <div style={{ fontSize: 11, color: "#8aa8c5", marginTop: 1 }}>
                {eq.fw}×{eq.fh} ft
              </div>

              {isLift(eq) && eq.variants && (
                <div style={{ display: "flex", gap: 2, flexWrap: "wrap", marginTop: 4 }}>
                  {eq.variants.map((v) => (
                    <span
                      key={v}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: eq.border,
                        background: `${eq.border}18`,
                        border: `1px solid ${eq.border}44`,
                        borderRadius: 3,
                        padding: "1px 4px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {v}
                    </span>
                  ))}
                </div>
              )}

              {!isLift(eq) && eq.requirements?.length > 0 && (
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 4 }}>
                  {eq.requirements.map((r) => (
                    <span
                      key={r}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: REQ_COLORS[r] || "#88aacc",
                        background: `${REQ_COLORS[r] || "#88aacc"}18`,
                        border: `1px solid ${REQ_COLORS[r] || "#88aacc"}44`,
                        borderRadius: 3,
                        padding: "1px 4px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Click-to-add — drops the item at the centre of the current view,
                selected, so it works without dragging (touch, trackpads, new users) */}
            {onAddItem && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddItem(eq.id); }}
                aria-label={`Add ${eq.name} to the floor plan`}
                title={`Add ${eq.name}`}
                style={{
                  alignSelf: "center", flexShrink: 0,
                  width: 30, height: 30, borderRadius: 7,
                  background: hovered?.id === eq.id ? "#0066cc" : "transparent",
                  border: `1px solid ${hovered?.id === eq.id ? "#3399ff" : "#1e3a54"}`,
                  color: hovered?.id === eq.id ? "#fff" : "#66b3ff",
                  fontSize: 18, lineHeight: 1, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                +
              </button>
            )}
          </div>
        ))}

        {/* Rooms section */}
        <div style={{ padding: "8px 6px", borderTop: "1px solid #162840", marginTop: 6 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#8aa8c5",
              letterSpacing: ".08em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Rooms & Areas
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addRoom(); }}
              placeholder="e.g. Office, Parts Room..."
              style={{
                flex: 1,
                background: "#040b14",
                border: "1px solid #162840",
                borderRadius: 4,
                color: "#e8f4ff",
                padding: "5px 8px",
                fontSize: 11,
                fontFamily: "inherit",
                outline: "none",
              }}
            />
            <button
              onClick={addRoom}
              title="Add a rectangular room"
              style={{
                background: "rgba(255,229,0,.1)",
                border: "1px solid rgba(255,229,0,.3)",
                borderRadius: 4,
                color: "rgba(255,229,0,.8)",
                fontSize: 14,
                fontWeight: 700,
                padding: "4px 10px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              +
            </button>
          </div>
          {/* Free-form room button — toggles polygon drawing mode in CadCanvas.
              While active (roomDrawMode true), the user clicks corners on the
              floor plan to outline a custom-shaped room. Clicking this button
              again, hitting ESC, or closing the polygon all exit drawing mode. */}
          {setRoomDrawMode && (
            <button
              onClick={() => setRoomDrawMode(!roomDrawMode)}
              title="Draw a polygon room by clicking corners on the canvas"
              style={{
                width: "100%",
                background: roomDrawMode ? "rgba(255,229,0,.2)" : "rgba(255,229,0,.05)",
                border: `1px solid ${roomDrawMode ? "rgba(255,229,0,.6)" : "rgba(255,229,0,.2)"}`,
                borderRadius: 4,
                color: roomDrawMode ? "rgba(255,229,0,1)" : "rgba(255,229,0,.7)",
                fontSize: 11,
                fontWeight: 600,
                padding: "6px 10px",
                cursor: "pointer",
                fontFamily: "inherit",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 12 }}>⬡</span>
              {roomDrawMode ? "Cancel drawing (ESC)" : "Add Free-form Room"}
            </button>
          )}
          <div style={{ fontSize: 11, color: "#2a4a6a", lineHeight: 1.5 }}>
            Type a name and press + or Enter.
            <br />
            Drag to position, resize with handles.
          </div>
        </div>

        {/* Floor drain */}
        <div style={{ padding: "8px 6px", borderTop: "1px solid #162840" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#8aa8c5",
              letterSpacing: ".08em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Floor Features
          </div>
          <button
            onClick={addDrain}
            style={{
              width: "100%",
              background: "rgba(0,150,200,.08)",
              border: "1px solid #0090c844",
              borderRadius: 6,
              color: "#00aacc",
              fontSize: 11,
              fontWeight: 600,
              padding: "7px 10px",
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
            }}
          >
            Add Floor Drain
          </button>
        </div>
      </div>

      {/* Bottom panel — selection info or hover tooltip (unchanged) */}
      <div
        style={{
          borderTop: "1px solid #162840",
          background: "#060d18",
          padding: 10,
          flexShrink: 0,
          fontSize: 11,
          color: "#8aa8c5",
          maxHeight: 400,
          overflow: "auto",
        }}
      >
        {multiSel && !selDrain ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#3399ff", marginBottom: 8 }}>
              ✦ {selItems.length} items selected
            </div>
            <FieldLabel>Align Horizontal</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginBottom: 8 }}>
              <AlignBtn onClick={() => align("left")} title="Align Left">⬛◻◻</AlignBtn>
              <AlignBtn onClick={() => align("centerH")} title="Center H">◻⬛◻</AlignBtn>
              <AlignBtn onClick={() => align("right")} title="Align Right">◻◻⬛</AlignBtn>
            </div>
            <FieldLabel>Align Vertical</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginBottom: 8 }}>
              <AlignBtn onClick={() => align("top")} title="Align Top">⬆</AlignBtn>
              <AlignBtn onClick={() => align("centerV")} title="Center V">↕</AlignBtn>
              <AlignBtn onClick={() => align("bottom")} title="Align Bottom">⬇</AlignBtn>
            </div>
            {selItems.length >= 3 && (
              <>
                <FieldLabel>Distribute</FieldLabel>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
                  <AlignBtn onClick={() => align("distH")} title="Distribute H">↔ H</AlignBtn>
                  <AlignBtn onClick={() => align("distV")} title="Distribute V">↕ V</AlignBtn>
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              <CtrlBtn onClick={() => rotate(-90)}>↺ 90°</CtrlBtn>
              <CtrlBtn onClick={() => rotate(90)}>↻ 90°</CtrlBtn>
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              <CtrlBtn onClick={() => setSelected([])}>✕ Deselect</CtrlBtn>
              <CtrlBtn danger onClick={remove}>🗑 Remove All</CtrlBtn>
            </div>
          </>
        ) : selItem && !selDrain ? (
          <>
            {selItem.isRoom ? (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,229,0,0.8)", marginBottom: 8 }}>
                  ⬜ {selItem.label || "Room"}
                </div>
                <FieldLabel>Room Name</FieldLabel>
                <input
                  type="text"
                  value={selItem.label || ""}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Office, Parts Room..."
                  style={inputS}
                />
                <FieldLabel>Colour</FieldLabel>
                <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
                  {ROOM_COLORS.map((rc) => (
                    <button
                      key={rc.label}
                      onClick={() =>
                        setPlaced((prev) =>
                          prev.map((p) =>
                            p.uid === selItem.uid ? { ...p, color: rc.color, border: rc.border } : p
                          )
                        )
                      }
                      title={rc.label}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        cursor: "pointer",
                        background: rc.color.replace("0.08", "0.5"),
                        border: `2px solid ${selItem.border === rc.border ? "#fff" : rc.border}`,
                      }}
                    />
                  ))}
                </div>
                <FieldLabel>Position from NW ({shop.unit})</FieldLabel>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 7 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>← East</div>
                    <input type="number" step="0.5" value={selItem.cx.toFixed(1)} onChange={(e) => setItemX(e.target.value)} style={inputS} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>↓ South</div>
                    <input type="number" step="0.5" value={selItem.cy.toFixed(1)} onChange={(e) => setItemY(e.target.value)} style={inputS} />
                  </div>
                </div>
                {/* Polygon rooms display their bbox dims as read-only info; resize
                    isn't supported in v1 (would need vertex editing or scaling math
                    around the centroid). Rect rooms keep the editable W/D inputs. */}
                {selItem.shapeType === 'free' ? (
                  <>
                    <FieldLabel>Shape</FieldLabel>
                    <div style={{
                      padding: "6px 8px",
                      background: "rgba(255,229,0,0.05)",
                      border: "1px solid rgba(255,229,0,0.2)",
                      borderRadius: 4,
                      fontSize: 11,
                      color: "#aaccee",
                      marginBottom: 8,
                      lineHeight: 1.4,
                    }}>
                      Polygon room — {selItem.vertices?.length || 0} corners,{" "}
                      {(selItem.fw || 0).toFixed(1)} × {(selItem.fh || 0).toFixed(1)} {shop.unit} bbox.
                      <br />
                      <span style={{ color: "#8aa8c5" }}>
                        Resize/rotate not available yet — delete and redraw to reshape.
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <FieldLabel>Size ({shop.unit})</FieldLabel>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 7 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>Width</div>
                        <input type="number" step="0.5" min="2" value={(selItem.fw * selItem.scaleX).toFixed(1)} onChange={(e) => setItemW(e.target.value)} style={inputS} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>Depth</div>
                        <input type="number" step="0.5" min="2" value={(selItem.fh * selItem.scaleY).toFixed(1)} onChange={(e) => setItemH(e.target.value)} style={inputS} />
                      </div>
                    </div>
                  </>
                )}
                <CtrlBtn danger onClick={remove}>✕ Remove Room</CtrlBtn>
              </>
            ) : (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#3399ff", marginBottom: 8 }}>
                  ✦ {selItem.name}
                </div>
                <FieldLabel>Bay / Custom Label</FieldLabel>
                <input type="text" value={selItem.label || ""} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Bay 1..." style={inputS} />
                <FieldLabel>Position from NW ({shop.unit})</FieldLabel>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 7 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>← East</div>
                    <input type="number" step="0.5" min="0" max={shop.w} value={selItem.cx.toFixed(1)} onChange={(e) => setItemX(e.target.value)} style={inputS} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>↓ South</div>
                    <input type="number" step="0.5" min="0" max={shop.h} value={selItem.cy.toFixed(1)} onChange={(e) => setItemY(e.target.value)} style={inputS} />
                  </div>
                </div>
                <FieldLabel>Size ({shop.unit})</FieldLabel>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 7 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>Width</div>
                    <input type="number" step="0.5" min="1" max={shop.w} value={(selItem.fw * selItem.scaleX).toFixed(1)} onChange={(e) => setItemW(e.target.value)} style={inputS} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>Depth</div>
                    <input type="number" step="0.5" min="1" max={shop.h} value={(selItem.fh * selItem.scaleY).toFixed(1)} onChange={(e) => setItemH(e.target.value)} style={inputS} />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <FieldLabel>Rotation</FieldLabel>
                  <span style={{ fontSize: 11, fontFamily: "monospace", color: "#c8dff0" }}>
                    {Math.round(selItem.rot)}°
                  </span>
                </div>
                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                  <CtrlBtn onClick={() => rotate(-90)}>↺ 90°</CtrlBtn>
                  <CtrlBtn onClick={() => rotate(90)}>↻ 90°</CtrlBtn>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <CtrlBtn onClick={reset}>⊡ Reset</CtrlBtn>
                  <CtrlBtn danger onClick={remove}>✕ Remove</CtrlBtn>
                </div>
              </>
            )}
          </>
        ) : selDrain ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#00aacc", marginBottom: 8 }}>
              Floor Drain
            </div>
            <FieldLabel>Label</FieldLabel>
            <input type="text" value={selDrain.label || ""} onChange={(e) => setDrainProp("label", e.target.value)} placeholder="Floor Drain" style={inputS} />
            <FieldLabel>Position from NW ({shop.unit})</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 7 }}>
              <div>
                <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>← East</div>
                <input type="number" step="0.5" value={selDrain.cx.toFixed(1)} onChange={(e) => setDrainProp("cx", parseFloat(e.target.value))} style={inputS} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>↓ South</div>
                <input type="number" step="0.5" value={selDrain.cy.toFixed(1)} onChange={(e) => setDrainProp("cy", parseFloat(e.target.value))} style={inputS} />
              </div>
            </div>
            <FieldLabel>Size ({shop.unit})</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 7 }}>
              <div>
                <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>Width</div>
                <input type="number" min={0.5} max={20} step={0.5} value={selDrain.w} onChange={(e) => setDrainProp("w", +e.target.value)} style={inputS} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 2 }}>Depth</div>
                <input type="number" min={0.5} max={20} step={0.5} value={selDrain.h} onChange={(e) => setDrainProp("h", +e.target.value)} style={inputS} />
              </div>
            </div>
            <CtrlBtn danger onClick={removeDrain}>✕ Remove Drain</CtrlBtn>
          </>
        ) : hovered ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: hovered.border || "#00cc88", marginBottom: 6 }}>
              {hovered.name}
            </div>
            <div style={{ fontSize: 11, color: "#8aa8c5", marginBottom: 4 }}>
              Footprint: {hovered.fw} × {hovered.fh} ft
            </div>
            {isLift(hovered) ? (
              <>
                <div style={{ fontSize: 11, color: "#c8dff0", marginBottom: 6 }}>
                  Capacity range:{" "}
                  <span style={{ color: hovered.border, fontWeight: 700 }}>{hovered.capacity}</span>
                </div>
                {hovered.requirements?.length > 0 && (
                  <div style={{ borderTop: "1px solid #0d1e30", paddingTop: 6 }}>
                    <div style={{ fontSize: 11, color: "#7f9fbd", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>
                      Requirements
                    </div>
                    {hovered.requirements.map((r) => (
                      <div key={r} style={{ fontSize: 11, color: REQ_COLORS[r] || "#88aacc", padding: "1px 0" }}>
                        • {r}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {hovered.isObstruction && (
                  <div style={{ fontSize: 11, color: "#cc3333", marginBottom: 4 }}>
                    Scale to size. Use for pillars, beams or fixed walls.
                  </div>
                )}
                {hovered.requirements?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: "#7f9fbd", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>
                      Requirements
                    </div>
                    {hovered.requirements.map((r) => (
                      <div key={r} style={{ fontSize: 11, color: REQ_COLORS[r] || "#88aacc", padding: "1px 0" }}>
                        • {r}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: "#8aa8c5", lineHeight: 1.7 }}>
            Click <b style={{ color: "#e8f4ff" }}>+</b> to add, or drag an item onto the plan.
            <br />
            Shift-click to select several · Delete removes.
          </div>
        )}
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#8aa8c5", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>
      {children}
    </div>
  );
}

function AlignBtn({ children, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: "rgba(0,80,180,.1)",
        border: "1px solid #1e3a54",
        borderRadius: 4,
        color: "#3399ff",
        fontSize: 13,
        padding: "5px 2px",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "center",
        width: "100%",
      }}
    >
      {children}
    </button>
  );
}

function CtrlBtn({ children, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        background: "transparent",
        border: `1px solid ${danger ? "#ff444433" : "#162840"}`,
        borderRadius: 4,
        color: danger ? "#ff6644" : "#a8c4dc",
        fontSize: 11,
        fontWeight: 500,
        padding: "5px 2px",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "center",
        display: "block",
        width: "100%",
        marginBottom: 4,
      }}
    >
      {children}
    </button>
  );
}

const inputS = {
  width: "100%",
  background: "#07111e",
  border: "1px solid #162840",
  borderRadius: 4,
  color: "#e8f4ff",
  padding: "5px 8px",
  fontSize: 11,
  fontFamily: "inherit",
  outline: "none",
  marginBottom: 6,
  display: "block",
  boxSizing: "border-box",
};
